import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { ClipboardList, Ship, Package, DollarSign, CheckCircle2, Clock, Loader2, Calendar, BarChart3, Boxes } from 'lucide-react';

const POManagement = () => {
    const [fromDate, setFromDate] = useState('2026-04-01');
    const [toDate, setToDate] = useState('2026-05-30');

    const stats = [
        { label: 'POs on Water', value: '23', sub: '14 vessels', icon: Ship, color: 'from-blue-500 to-cyan-500', gradient: 'from-blue-500/20 to-cyan-500/20', border: 'border-blue-400/50' },
        { label: 'Fully Loaded', value: '18', sub: 'Ready to sail', icon: Package, color: 'from-emerald-500 to-green-500', gradient: 'from-emerald-500/20 to-green-500/20', border: 'border-emerald-400/50' },
        { label: 'Partially Loaded', value: '5', sub: 'Awaiting cargo', icon: Loader2, color: 'from-amber-500 to-orange-500', gradient: 'from-amber-500/20 to-orange-500/20', border: 'border-amber-400/50' },
        { label: 'PO Value', value: '$50.8M', sub: '+12% vs last', icon: DollarSign, color: 'from-purple-500 to-indigo-500', gradient: 'from-purple-500/20 to-indigo-500/20', border: 'border-purple-400/50' },
    ];

    const poOrders = [
        { po: 'PO-4521', supplier: 'Shanghai Elec.', items: '12 items', value: '$48.5K', loadStatus: 'full', containers: '2x 40HC', vessel: 'MSC OSCAR', eta: 'May 22', phase: 'on_water', phaseLabel: 'On Water' },
        { po: 'PO-4519', supplier: 'Thai Chin Fu', items: '8 items', value: '$32.2K', loadStatus: 'full', containers: '1x 40HC', vessel: 'COSCO UNIV', eta: 'May 18', phase: 'arriving', phaseLabel: 'Arriving' },
        { po: 'PO-4517', supplier: 'Rotterdam Chem', items: '3 items', value: '$125K', loadStatus: 'partial', containers: '1x 20GP', vessel: 'Pending', eta: 'Jun 05', phase: 'loading', phaseLabel: 'Loading' },
        { po: 'PO-4515', supplier: 'Mumbai Textile', items: '25 items', value: '$67.8K', loadStatus: 'full', containers: '3x 40HC', vessel: 'EVER GIVEN', eta: 'May 28', phase: 'on_water', phaseLabel: 'On Water' },
        { po: 'PO-4513', supplier: 'Korea Auto', items: '6 items', value: '$89.3K', loadStatus: 'full', containers: '2x 40GP', vessel: 'HAPAG EXP', eta: 'May 10', phase: 'delivered', phaseLabel: 'Delivered' },
    ];

    const phaseConfig = {
        on_water: {
            color: 'bg-blue-500',
            text: 'text-blue-700 dark:text-blue-400',
            bg: 'bg-blue-50/80 dark:bg-blue-500/10',
            border: 'border-blue-400/50 dark:border-blue-500/30',
            dot: 'bg-blue-500',
            icon: '🚢'
        },
        arriving: {
            color: 'bg-cyan-500',
            text: 'text-cyan-700 dark:text-cyan-400',
            bg: 'bg-cyan-50/80 dark:bg-cyan-500/10',
            border: 'border-cyan-400/50 dark:border-cyan-500/30',
            dot: 'bg-cyan-500',
            icon: '📍'
        },
        loading: {
            color: 'bg-amber-500',
            text: 'text-amber-700 dark:text-amber-400',
            bg: 'bg-amber-50/80 dark:bg-amber-500/10',
            border: 'border-amber-400/50 dark:border-amber-500/30',
            dot: 'bg-amber-500',
            icon: '📦'
        },
        delivered: {
            color: 'bg-emerald-500',
            text: 'text-emerald-700 dark:text-emerald-400',
            bg: 'bg-emerald-50/80 dark:bg-emerald-500/10',
            border: 'border-emerald-400/50 dark:border-emerald-500/30',
            dot: 'bg-emerald-500',
            icon: '✅'
        },
    };

    return (
        <motion.div
            initial={{ y: 20, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            transition={{ duration: 0.5, delay: 0.3 }}
            className="rounded-xl shadow-lg overflow-hidden bg-white/70 dark:bg-slate-900/60 backdrop-blur-xl border border-slate-300/70 dark:border-white/15 h-full flex flex-col"
            data-testid="po-management"
        >
            {/* Header - Compact */}
            <div className="px-4 py-2.5 border-b border-slate-300/50 dark:border-white/15 bg-gradient-to-r from-white/40 to-white/20 dark:from-white/5 dark:to-transparent">
                <div className="flex items-center justify-between gap-2">
                    <div className="flex items-center gap-2">
                        <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-blue-500 to-indigo-600 text-white flex items-center justify-center">
                            <Boxes className="w-5 h-5" />
                        </div>
                        <div>
                            <h3 className="text-sm font-bold text-slate-800 dark:text-white">PO Fulfillment</h3>
                            <p className="text-[10px] text-slate-600 dark:text-slate-400">Supplier to warehouse</p>
                        </div>
                    </div>
                    <div className="flex items-center gap-1.5 bg-white/60 dark:bg-white/10 backdrop-blur-sm rounded-lg px-2.5 py-1 border border-slate-400/50 dark:border-white/15">
                        <Calendar className="w-3.5 h-3.5 text-slate-500" />
                        <input
                            type="date"
                            value={fromDate}
                            onChange={e => setFromDate(e.target.value)}
                            className="bg-transparent text-[11px] text-slate-700 dark:text-slate-300 border-none outline-none w-24"
                        />
                        <span className="text-slate-400 text-[11px]">—</span>
                        <input
                            type="date"
                            value={toDate}
                            onChange={e => setToDate(e.target.value)}
                            className="bg-transparent text-[11px] text-slate-700 dark:text-slate-300 border-none outline-none w-24"
                        />
                    </div>
                </div>
            </div>

            {/* Stats Grid - Compact with larger text */}
            <div className="grid grid-cols-4 gap-2 p-3 bg-gradient-to-b from-transparent to-slate-100/30 dark:to-white/5 border-b border-slate-300/50 dark:border-white/15">
                {stats.map((s, i) => (
                    <motion.div
                        key={i}
                        initial={{ opacity: 0, y: 10 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: i * 0.1 }}
                        className={`relative overflow-hidden rounded-lg bg-gradient-to-br ${s.gradient} backdrop-blur-sm border ${s.border} p-2.5`}
                    >
                        <div className="flex items-center justify-between mb-1.5">
                            <div className={`w-7 h-7 rounded-lg bg-gradient-to-br ${s.color} flex items-center justify-center shadow-sm`}>
                                <s.icon className="w-3.5 h-3.5 text-white" />
                            </div>
                            <div className="absolute top-1.5 right-1.5 opacity-10">
                                <s.icon className="w-8 h-8" />
                            </div>
                        </div>
                        <p className="text-lg font-bold text-slate-800 dark:text-white">{s.value}</p>
                        <p className="text-[10px] font-semibold text-slate-700 dark:text-slate-300 mt-0.5">{s.label}</p>
                        <p className="text-[9px] text-slate-500 dark:text-slate-400">{s.sub}</p>
                    </motion.div>
                ))}
            </div>

            {/* PO List - Compact with better text size */}
            <div className="divide-y divide-slate-200/60 dark:divide-white/10 flex-1 overflow-auto">
                {poOrders.map((po, i) => {
                    const pc = phaseConfig[po.phase];
                    return (
                        <motion.div
                            key={po.po}
                            initial={{ opacity: 0, x: -10 }}
                            animate={{ opacity: 1, x: 0 }}
                            transition={{ delay: i * 0.06 }}
                            className="mx-2 my-1.5 px-3 py-2 rounded-lg transition-all duration-200 bg-white/40 dark:bg-white/5 backdrop-blur-sm border border-slate-300/70 dark:border-white/10 hover:bg-white/60 dark:hover:bg-white/10"
                        >
                            <div className="flex items-center justify-between gap-2">
                                {/* Left Section */}
                                <div className="flex items-center gap-2 flex-1 min-w-0">
                                    <div className={`w-2 h-2 rounded-full ${pc.dot} flex-shrink-0 shadow-sm`} />
                                    <div className="min-w-0 flex-1">
                                        <div className="flex items-center gap-1.5 flex-wrap">
                                            <span className="text-xs font-bold text-slate-800 dark:text-white">{po.po}</span>
                                            <span className={`inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded-full text-[9px] font-semibold backdrop-blur-sm ${pc.bg} ${pc.text} border ${pc.border}`}>
                                                <span className="text-[10px]">{pc.icon}</span>
                                                {po.phaseLabel}
                                            </span>
                                        </div>
                                        <div className="flex items-center gap-2 mt-0.5 text-[10px] text-slate-600 dark:text-slate-400 flex-wrap">
                                            <span className="font-medium text-slate-700 dark:text-slate-300">{po.supplier}</span>
                                            <span className="text-[9px]">{po.items}</span>
                                            <span className="flex items-center gap-0.5 text-[9px]">
                                                <Package className="w-2.5 h-2.5" />
                                                {po.containers}
                                            </span>
                                        </div>
                                    </div>
                                </div>

                                {/* Right Section */}
                                <div className="flex items-center gap-2 flex-shrink-0">
                                    <div className="text-right">
                                        <p className="text-xs font-bold text-slate-800 dark:text-white">{po.value}</p>
                                        {po.vessel !== 'Pending' && (
                                            <p className="text-[8px] text-slate-500 dark:text-slate-400 flex items-center gap-0.5 justify-end">
                                                <Ship className="w-2 h-2" />
                                                {po.vessel.split(' ')[0]}
                                            </p>
                                        )}
                                    </div>
                                    <div className="text-right min-w-[55px]">
                                        <p className="text-[8px] text-slate-500 dark:text-slate-400">ETA</p>
                                        <p className="text-[10px] font-semibold text-slate-700 dark:text-slate-300">{po.eta}</p>
                                    </div>
                                    <div className={`w-6 h-6 rounded-md flex items-center justify-center backdrop-blur-sm border ${po.loadStatus === 'full'
                                        ? 'bg-emerald-500/15 text-emerald-600 dark:text-emerald-400 border-emerald-400/50 dark:border-emerald-500/30'
                                        : 'bg-amber-500/15 text-amber-600 dark:text-amber-400 border-amber-400/50 dark:border-amber-500/30'
                                        }`}>
                                        {po.loadStatus === 'full'
                                            ? <CheckCircle2 className="w-3 h-3" />
                                            : <Clock className="w-3 h-3" />
                                        }
                                    </div>
                                </div>
                            </div>

                            {/* Progress Bar - Thinner */}
                            {po.phase !== 'delivered' && (
                                <div className="mt-1.5">
                                    <div className="h-0.5 bg-slate-200/60 dark:bg-slate-700/40 rounded-full overflow-hidden">
                                        <motion.div
                                            initial={{ width: 0 }}
                                            animate={{ width: po.phase === 'on_water' ? '65%' : po.phase === 'arriving' ? '85%' : '25%' }}
                                            transition={{ duration: 1, delay: i * 0.1 }}
                                            className={`h-full rounded-full bg-gradient-to-r ${po.phase === 'on_water' ? 'from-blue-500 to-cyan-400' :
                                                po.phase === 'arriving' ? 'from-cyan-500 to-teal-400' :
                                                    'from-amber-500 to-yellow-400'
                                                } opacity-80`}
                                        />
                                    </div>
                                </div>
                            )}
                        </motion.div>
                    );
                })}
            </div>
        </motion.div>
    );
};

export default POManagement;